import { useId } from "react";

import { cn } from "@/lib/utils";

import type { GameType } from "./types";

function FlashIcon({ className, ...props }: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-[21px] w-[17px]", className)}
      fill="none"
      viewBox="0 0 17 21"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <path
          clipRule="evenodd"
          d="M8.369 3.544l-.167.308a10615.463 10615.463 0 01-2.574 4.755 1440.348 1440.348 0 01-1.196 2.209c-.087.16.034.353.223.353l.974.002a62.79 62.79 0 011.389.01 445.73 445.73 0 00-.26.486l-.224.414a2168.863 2168.863 0 01-1.404 2.592l-.286.53a3211.762 3211.762 0 01-1.242 2.29c-.014.024.03-.01.173-.13a396.449 396.449 0 011.182-.989 2333.308 2333.308 0 013.18-2.66 697.984 697.984 0 011.644-1.375 481.27 481.27 0 011.618-1.352l1.14-.954c.076-.063.03-.182-.07-.182l-1.1-.002a68.652 68.652 0 01-1.387-.01c0-.004.076-.147.169-.319a610.062 610.062 0 00.82-1.514 3586.066 3586.066 0 001.845-3.407l.36-.665c.123-.227.224-.417.224-.423 0-.008-.735-.011-2.504-.011H8.444a.084.084 0 00-.075.044z"
          fill={`url(#${id}b)`}
          fillRule="evenodd"
        />
      </g>
      <g filter={`url(#${id}c)`}>
        <path
          clipRule="evenodd"
          d="M11.967 3.5l-3.818 7h2.8l-7.35 7 .03-.018c.028-.02.075-.059.145-.118l.588-.492.595-.498L6.48 15.1l.255-.214.806-.674.595-.498.535-.448a762.781 762.781 0 001.526-1.275l.562-.47a733.5 733.5 0 001.78-1.488c.075-.063.028-.182-.071-.182l-1.1-.002a68.56 68.56 0 01-1.388-.01c0-.004.076-.147.17-.319l.317-.586.503-.928.51-.944a706.978 706.978 0 011.037-1.913l.297-.55.361-.666c.123-.226.223-.416.223-.422 0-.006-.432-.01-1.432-.01z"
          fill={`url(#${id}d)`}
          fillRule="evenodd"
        />
      </g>
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="3.858"
          x2="17.049"
          y1="16.567"
          y2="7.134"
        >
          <stop stopColor="#ffcf55" />
          <stop offset="1" stopColor="#bd2885" />
        </linearGradient>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}d`}
          x1="2.808"
          x2="15.998"
          y1="17.267"
          y2="7.835"
        >
          <stop stopColor="#c79b29" />
          <stop offset="1" stopColor="#bd2885" />
        </linearGradient>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="19.6"
          id={`${id}a`}
          width="15.4"
          x="0.8"
          y="0.7"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="1.4" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0.929348 0 0 0 0 0.44144 0 0 0 0 0.524009 0 0 0 0.1 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_18909_46174"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_18909_46174"
            result="shape"
          />
        </filter>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="19.599"
          id={`${id}c`}
          width="15.4"
          x="0.8"
          y="0.7"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="1.4" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0.929348 0 0 0 0 0.44144 0 0 0 0 0.524009 0 0 0 0.2 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_18909_46174"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_18909_46174"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

interface BookingsBannerProps {
  game: GameType;
}

export default function BookingsBanner({ game }: BookingsBannerProps) {
  if (!game.hasBookings) {
    return null;
  }

  return (
    <div className="flex h-[33px] items-center justify-center gap-1.5 bg-linear-71 from-mystic-red/10 to-electric-purple/10 md:absolute md:inset-x-0 md:top-127 md:h-11">
      <FlashIcon />
      <p className="font-semibold text-14 text-white md:text-16">
        Instantly book on Morty
      </p>
    </div>
  );
}
